import { getDataReaderClient } from "@multiweb/clients/util";
import { IRowData } from "@multiweb/types/contracts";
import { DataSourceErrorHandler } from "UI/UI/UI/DataSourceErrorHandler";
import { IDataSource, IDataSourceLoadInfo, IDBDataSourceParams, IOrderBy } from "../../../Global/Base/DataSources/Interfaces";
import { Event } from "../../../Global/Base/Events";
import { InvalidOperationException } from "../../../Global/Base/Exceptions";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { ConditionOperator, StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../OW.Remoting/Remoting/AttrValueReader";
import { IDBDataSourceLoadParams } from "./DBDataSource";
import { EditableDBDataSource } from "./EditableDBDataSource";

export interface ITreeViewDataSourceParams extends IDBDataSourceParams {
    hierarchyAttr: string;
}

/**
 * Trida reprezentujici databazovy zdroj dat pro konzumenta dat
 */
export class TreeViewDataSource extends EditableDBDataSource {
    //#region Events
    /**
     * Vraci startindex pro cteni dat
     */
    public override dataLoaded: Event<IDataSource, ITreeDataSourceLoadInfo> = new Event<IDataSource, ITreeDataSourceLoadInfo>();
    //#endregion Events

    //#region Fields
    private _hierarchyAttr: string;
    public rootId: string = "0";

    protected override get _internalRows(): readonly ITreeViewRowData[] {
        return super._internalRows as readonly ITreeViewRowData[];
    }
    protected override set _internalRows(value: readonly ITreeViewRowData[]) {
        super._internalRows = value;
    }

    private _reloadData: boolean = false;

    public override get orderBy(): IOrderBy[] {
        return this._orderBy;
    }

    public override set orderBy(values: IOrderBy[]) {
        this._orderBy = values;
        if (values && values.length > 0) {
            const resultOrderBy: string[] = [];
            let dataKeyPresent: boolean = false;
            const upperDataKey = this.dataKey.toLocaleUpperCase();

            for (const orderByItem of values) {
                resultOrderBy.push(this._parseOrderBy(orderByItem));
                if (orderByItem.attributeName.toLocaleUpperCase() === upperDataKey)
                    dataKeyPresent = true;
            }

            // Pokud není obsažen dataKey, přidá se. To zajistí stejné setřídění dat, pokud je v hodnotách nastavovaných atributů hodně opakujících se hodnot.
            if (!dataKeyPresent)
                resultOrderBy.push(this.dataKey);

            this._internalOrderBy = resultOrderBy;
        } else {
            this._internalOrderBy = [this.dataKey]; // při nezadaném sloupci pro třídění se defaultně použije sloupec ID
        }
    }

    //#region Constructors
    constructor(params: ITreeViewDataSourceParams) {
        super(params);
        this._hierarchyAttr = params.hierarchyAttr;

        this.error.add((_sender, error) => {
            DataSourceErrorHandler.handleDataSourceError(error, this.hasFilterCondition);
        });
    }
    //#endregion Constructors

    protected override *_getIterator(startIndex: number, count?: number, parentId: string = "0"): IterableIterator<ITreeViewRowData> {
        const preRow = this._internalRows.filter((item) => item.parentId == parentId);
        const maxIndex = count ? Math.min(startIndex + count, preRow.length) : preRow.length;
        for (let i = startIndex ? startIndex : 0; i < maxIndex; i++)
            yield preRow[i];
    }

    public loadChildData(parentId: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return this._loadTreeData({}, parentId, cancellationToken);
    }

    /**
     * Nahraje data od zacatku
     */
    public override loadNewData(_keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        this._reloadData = true;
        return this._loadTreeData({}, undefined, cancellationToken);
    }

    public async getObjectHierarchy(dataKey: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<ITreeViewRowData> {
        const condition = `${this.dataKey} = ${dataKey}`; //`
        const rows = await getDataReaderClient().getDataRowsWithParams(this._className, this._attributes.concat(this._hierarchyAttr), condition, undefined, undefined, this._getRequestContext(cancellationToken));
        if (rows.length > 0) {
            return { parentId: AttrValueReader.readRow(rows[0], this._hierarchyAttr), hasChildren: false, data: rows[0] };
        } else {
            return null;
        }
    }

    /**
     *
     * @param startIndexForEvent
     * @param countForEvent
     */
    protected _loadTreeData(params: IDBDataSourceLoadParams, hierarchyId: string = null, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        if (/@\d+/.test(this._condition) && (this._conditionParams == null || this._conditionParams.length == 0))
            return Promise.reject(new InvalidOperationException("Condition contains params but no parameter value available"));

        let conditionHierarchy = "";
        if (hierarchyId)
            conditionHierarchy = `${this._hierarchyAttr} = ${hierarchyId}`; // `
        else
            conditionHierarchy = `NOT EXISTS (${this._className}, id = &${this._hierarchyAttr} AND id <> 0 AND ${this._condition}) AND id <> 0`; // `

        this._runningDataLoadCTS = new CancellationTokenSource([cancellationToken]);
        this._runningDataLoad = getDataReaderClient().getDataRowsWithParams(this._className, this._attributes, StringUtils.combineConditions(ConditionOperator.and, this._condition, conditionHierarchy), {
            params: this._conditionParams,
            orderBy: this._internalOrderBy,
            readVersioned: this._readVersioned,
            distinct: this.useDistinct,
            groupBy: this._groupBy,

        }, this._fullLengthAttributes, this._getRequestContext(this._runningDataLoadCTS.token))
            .then(async (rows) => {
                const preRow = rows.map((item) => {
                    return { parentId: hierarchyId, hasChildren: false, data: item };
                });
                this._internalRows = (hierarchyId == null) ? this._internalRows = preRow : (this._internalRows || []).concat(preRow);

                if (preRow.length > 0) {
                    let condition = "";
                    if (hierarchyId)
                        condition = `${this._condition} AND ${this._hierarchyAttr}.${this._hierarchyAttr} = ${hierarchyId}`; //`
                    else
                        condition = `${this._condition} AND NOT EXISTS (${this._className}, id = &${this._hierarchyAttr}.${this._hierarchyAttr} AND id <> 0 AND ${this._condition}) AND id <> 0`; //`
                    const subRows = await getDataReaderClient().getDataRowsWithParams(this._className, [this._hierarchyAttr], condition,
                        {
                            params: this._conditionParams,
                            distinct: true,
                        }, this._fullLengthAttributes, this._getRequestContext());
                    for (const iRow of preRow) {
                        if (subRows.some((item) => AttrValueReader.readRow(item, this._hierarchyAttr) == AttrValueReader.readRow(iRow.data, "ID")))
                            iRow.hasChildren = true;
                    }
                }

                const dsLoadInfo: ITreeDataSourceLoadInfo = {
                    startIndex: params.startIndexForEvent,
                    dataGenerator: this._getIterator.bind(this, params.startIndexForEvent, params.countForEvent, hierarchyId),
                    lastBatch: true,
                    isFiltered: this.hasFilterCondition,
                    fulltextSearchString: this.fulltextCondition?.searchString,
                    refreshedData: this._reloadData,
                    isRoot: !hierarchyId || hierarchyId == this.rootId,
                    emptyData: !preRow.length,
                };

                this.dataLoaded.trigger(this, dsLoadInfo);
                this.loadingEnded.trigger(this, dsLoadInfo);

                if (this._reloadData)
                    this._reloadData = false;
            }).catch((err) => {
                this.loadingEnded.trigger(this, { isFiltered: this.hasFilterCondition, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true, cancellationToken: cancellationToken });
                // zrušení nahrávání data je v pořádku
                if (!(err instanceof CancelError))
                    this.error.trigger(this, err);
            });

        this.loadingStarted.trigger(this, this._startIndex);

        return this._runningDataLoad;
    }

    public override refreshData(_fetchCount?: number, _keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        this._internalRows = [];
        this._reloadData = true;
        return this._loadTreeData({}, undefined, cancellationToken);
    }
}

export interface ITreeViewRowData extends IRowData {
    parentId: string;
    hasChildren: boolean;
    data: IRowData;
    [nope: string]: any;
}

/**
 * Obsahuje základní informace o načtených datech.
 * @generateInstanceClass
 */
export interface ITreeDataSourceLoadInfo extends IDataSourceLoadInfo {
    /**
     * Příznak toho zda se načítá první větev
     */
    isRoot: boolean;
}

export class ITreeDataSourceLoadInfo {
    private static _properties = ["isRoot"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IDataSourceLoadInfo;
    }
}
